import React, { useState, useEffect } from 'react';
import { API_URL } from '../config/apiConfig';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    TextField,
    InputAdornment,
    List,
    ListItem,
    ListItemText,
    CircularProgress,
    Alert,
    Paper,
    Divider,
    ToggleButton,
    ToggleButtonGroup,
    Chip,
    useTheme,
    useMediaQuery
} from '@mui/material';
import { styled } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PublicIcon from '@mui/icons-material/Public';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import MaleIcon from '@mui/icons-material/Male';
import FemaleIcon from '@mui/icons-material/Female';

const StyledListItem = styled(ListItem)(({ theme }) => ({
    borderRadius: theme.shape.borderRadius,
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.mode === 'dark' ? '#1e1e1e' : '#f5f5f5',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    '&:hover': {
        backgroundColor: theme.palette.mode === 'dark' ? '#2e2e2e' : '#e0e0e0',
        transform: 'translateY(-1px)',
    },
}));

const RankChip = styled(Chip)(({ theme, rank }) => {
    let backgroundColor, color;

    switch (rank) {
        case 1:
            backgroundColor = '#FFD700'; // Gold
            color = '#000';
            break;
        case 2:
            backgroundColor = '#C0C0C0'; // Silver
            color = '#000';
            break;
        case 3:
            backgroundColor = '#CD7F32'; // Bronze
            color = '#fff';
            break;
        default:
            backgroundColor = theme.palette.grey[300];
            color = theme.palette.text.primary;
    }

    return {
        backgroundColor,
        color,
        fontWeight: 'bold',
        minWidth: '40px',
    };
});

const RecordDetailModal = ({ open, onClose, recordName, records, scoreType, setScoreType, gender, setGender }) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const handleScoreTypeChange = (event, newScoreType) => {
        if (newScoreType) {
            setScoreType(newScoreType);
        }
    };

    const handleGenderChange = (event, newGender) => {
        if (newGender) {
            setGender(newGender);
        }
    };

    // Filter records by gender
    const filteredRecords = records.filter(record => {
        if (gender === 'men') {
            // Include male, null, other, or undefined sex values
            return !record.user.sex || record.user.sex === 'male' || record.user.sex === 'other';
        } else {
            // Include only female sex values
            return record.user.sex === 'female';
        }
    });

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            fullScreen={isMobile}
            sx={{
                '& .MuiDialog-paper': {
                    ...(isMobile && {
                        margin: 0,
                        width: '100%',
                        height: '100%',
                        maxHeight: '100%',
                        borderRadius: 0
                    })
                }
            }}
        >
            <DialogTitle sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                fontWeight: 'bold'
            }}>
                <EmojiEventsIcon />
                {recordName} - Leaderboard
            </DialogTitle>
            <DialogContent>
                <Box sx={{ mb: 3 }}>
                    {/* Score Type Toggle */}
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                            Category
                        </Typography>
                        <ToggleButtonGroup
                            color="primary"
                            value={scoreType}
                            exclusive
                            onChange={handleScoreTypeChange}
                            aria-label="score type"
                            size="small"
                            sx={{
                                display: 'flex',
                                width: isMobile ? '100%' : 'auto',
                                '& .MuiToggleButton-root': {
                                    flex: isMobile ? 1 : 'none'
                                }
                            }}
                        >
                            <ToggleButton value="rx">RX</ToggleButton>
                            <ToggleButton value="sc">Scaled</ToggleButton>
                            <ToggleButton value="beg">Beginner</ToggleButton>
                        </ToggleButtonGroup>
                    </Box>

                    {/* Gender Toggle */}
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                            Gender
                        </Typography>
                        <ToggleButtonGroup
                            color="secondary"
                            value={gender}
                            exclusive
                            onChange={handleGenderChange}
                            aria-label="gender"
                            size="small"
                            sx={{
                                display: 'flex',
                                width: isMobile ? '100%' : 'auto',
                                '& .MuiToggleButton-root': {
                                    flex: isMobile ? 1 : 'none',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                }
                            }}
                        >
                            <ToggleButton value="men">
                                <MaleIcon fontSize="small" />
                                Men
                            </ToggleButton>
                            <ToggleButton value="women">
                                <FemaleIcon fontSize="small" />
                                Women
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Box>
                </Box>

                {filteredRecords.length === 0 ? (
                    <Typography variant="body1" sx={{ textAlign: 'center', py: 4 }}>
                        No records found for this category and gender.
                    </Typography>
                ) : (
                    <List sx={{ width: '100%' }}>
                        {filteredRecords.map((record, index) => (
                            <Box key={record.id} sx={{ mb: 2 }}>
                                <Paper
                                    elevation={2}
                                    sx={{
                                        p: 2,
                                        border: index < 3 ? `2px solid ${
                                            index === 0 ? '#FFD700' :
                                                index === 1 ? '#C0C0C0' : '#CD7F32'
                                        }` : 'none'
                                    }}
                                >
                                    <Box sx={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        mb: 1
                                    }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                            <RankChip
                                                label={`#${index + 1}`}
                                                rank={index + 1}
                                                size="small"
                                            />
                                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                {record.user.fullName}
                                            </Typography>
                                        </Box>
                                        <Typography
                                            variant="h5"
                                            sx={{
                                                fontWeight: 'bold',
                                                color: theme.palette.primary.main
                                            }}
                                        >
                                            {record.score}
                                        </Typography>
                                    </Box>

                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                        {new Date(record.date).toLocaleDateString()}
                                    </Typography>

                                    {record.comment && (
                                        <Typography variant="body2" sx={{
                                            fontStyle: 'italic',
                                            backgroundColor: theme.palette.mode === 'dark' ? '#2e2e2e' : '#f5f5f5',
                                            p: 1,
                                            borderRadius: 1,
                                            mt: 1
                                        }}>
                                            "{record.comment}"
                                        </Typography>
                                    )}
                                </Paper>
                            </Box>
                        ))}
                    </List>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
};

const PublicWODRecords = ({ open, onClose }) => {
    const [records, setRecords] = useState({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [showRecords, setShowRecords] = useState(null);
    const [needsPermission, setNeedsPermission] = useState(false);

    // Modal state for individual WOD records
    const [selectedWOD, setSelectedWOD] = useState(null);
    const [wodRecords, setWodRecords] = useState([]);
    const [scoreType, setScoreType] = useState('rx');
    const [gender, setGender] = useState('men'); // Default to men
    const [wodModalOpen, setWodModalOpen] = useState(false);
    const [wodLoading, setWodLoading] = useState(false);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    // API_URL imported from config

    useEffect(() => {
        if (open) {
            loadPublicRecords();
        }
    }, [open]);

    const loadPublicRecords = async () => {
        try {
            setError('');
            setLoading(true);
            setNeedsPermission(false);

            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/records/public/wod`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            const data = await response.json();

            if (!response.ok) {
                if (response.status === 403) {
                    setNeedsPermission(true);
                    setShowRecords(data.showRecords);
                } else {
                    throw new Error(data.error || 'Error fetching public records');
                }
                return;
            }

            setRecords(data.records);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleMakeRecordsPublic = async () => {
        try {
            setError('');
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/records/user/show-records`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ showRecords: true })
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to update records visibility');
            }

            setShowRecords(true);
            setNeedsPermission(false);
            loadPublicRecords();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleWODClick = async (wodName) => {
        try {
            setWodLoading(true);
            setSelectedWOD(wodName);
            setScoreType('rx');
            setGender('men'); // Reset to default when opening new WOD

            console.log('Clicking WOD:', wodName); // Debug log

            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/records/public/wod/${encodeURIComponent(wodName)}?scoreType=rx`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Error fetching WOD records');
            }

            const data = await response.json();
            console.log('WOD records response:', data); // Debug log
            setWodRecords(data.records);
            setWodModalOpen(true);
        } catch (err) {
            console.log('Error in handleWODClick:', err); // Debug log
            setError(err.message);
        } finally {
            setWodLoading(false);
        }
    };

    const loadWODRecordsByScoreType = async (newScoreType) => {
        try {
            setWodLoading(true);

            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/records/public/wod/${encodeURIComponent(selectedWOD)}?scoreType=${newScoreType}`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                }
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Error fetching WOD records');
            }

            const data = await response.json();
            setWodRecords(data.records);
        } catch (err) {
            setError(err.message);
        } finally {
            setWodLoading(false);
        }
    };

    useEffect(() => {
        if (selectedWOD && wodModalOpen) {
            loadWODRecordsByScoreType(scoreType);
        }
    }, [scoreType]);

    // Filter records based on search query
    const filteredWODNames = Object.keys(records).filter(name =>
        searchQuery.length < 2 || name.toLowerCase().includes(searchQuery.toLowerCase())
    ).sort();

    const handleClose = () => {
        setRecords({});
        setSearchQuery('');
        setError('');
        setNeedsPermission(false);
        setShowRecords(null);
        setWodModalOpen(false);
        setSelectedWOD(null);
        setWodRecords([]);
        setScoreType('rx');
        setGender('men');
        onClose();
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                fullScreen={useMediaQuery(useTheme().breakpoints.down('md'))}
                sx={{
                    '& .MuiDialog-paper': {
                        ...(useMediaQuery(useTheme().breakpoints.down('md')) && {
                            margin: 0,
                            width: '100%',
                            height: '100%',
                            maxHeight: '100%',
                            borderRadius: 0
                        })
                    }
                }}
            >
                <DialogTitle sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    fontWeight: 'bold'
                }}>
                    <PublicIcon />
                    Public WOD Records
                </DialogTitle>
                <DialogContent>
                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {needsPermission ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <VisibilityOffIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="h6" gutterBottom>
                                Make Your Records Public
                            </Typography>
                            <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                                To view other users' WOD records, you need to make your own records public first.
                                This creates a fair exchange where everyone who views records also shares theirs.
                            </Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={handleMakeRecordsPublic}
                                startIcon={<PublicIcon />}
                                size="large"
                            >
                                Make My WOD Records Public
                            </Button>
                        </Box>
                    ) : (
                        <>
                            <TextField
                                placeholder="Search WODs... (min 2 characters)"
                                variant="outlined"
                                size="small"
                                fullWidth
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                sx={{ mb: 3 }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />

                            {loading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                                    <CircularProgress />
                                </Box>
                            ) : (
                                <>
                                    {filteredWODNames.length === 0 ? (
                                        <Typography variant="body1" sx={{ textAlign: 'center', py: 4 }}>
                                            {searchQuery.length >= 2 ? 'No WODs found matching your search.' : 'Start typing to search WODs...'}
                                        </Typography>
                                    ) : (
                                        <List sx={{ width: '100%' }}>
                                            {filteredWODNames.map((wodName) => (
                                                <StyledListItem
                                                    key={wodName}
                                                    onClick={() => handleWODClick(wodName)}
                                                    disabled={wodLoading}
                                                >
                                                    <ListItemText
                                                        primary={
                                                            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                                                {wodName}
                                                            </Typography>
                                                        }
                                                        secondary={
                                                            <Typography variant="body2" color="text.secondary">
                                                                {Object.keys(records[wodName]).map(scoreType =>
                                                                    `${Object.keys(records[wodName][scoreType]).length} ${scoreType.toUpperCase()}`
                                                                ).join(' • ')} participants
                                                            </Typography>
                                                        }
                                                    />
                                                </StyledListItem>
                                            ))}
                                        </List>
                                    )}
                                </>
                            )}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Close</Button>
                </DialogActions>
            </Dialog>

            {/* WOD Detail Modal */}
            <RecordDetailModal
                open={wodModalOpen}
                onClose={() => setWodModalOpen(false)}
                recordName={selectedWOD}
                records={wodRecords}
                scoreType={scoreType}
                setScoreType={setScoreType}
                gender={gender}
                setGender={setGender}
            />
        </>
    );
};

export default PublicWODRecords;